import { ApiError } from '~/api/errors';

/**
 * Feature detection for catalog-service reads newer than some deployed backends (api-changes §48 /
 * §49, §50). Each new read is tried once; a "this route does not exist" answer
 * marks it unavailable for the rest of the app session, and callers use today's read path
 * instead — no error is shown and the route is not probed again until the page reloads.
 */
export type CatalogCapability =
  /** `GET catalog-products/{id}/variants` (§48). */
  | 'productVariants'
  /** `GET catalog-runs/{id}/lineage/listings` (§49). */
  | 'lineageListings'
  /** `GET catalog-runs/{id}/lineage/issues` + `/issues/listings` (§49). */
  | 'lineageIssues'
  /** `GET experiments/status-counts` (§49). */
  | 'experimentStatusCounts'
  /** `POST images/usages/query` (§50). */
  | 'assetUsagesQuery'
  /** `GET needs-attention/counts` (§50). */
  | 'needsAttentionCounts'
  /** `GET catalog-operations/{ref}/delivery/rejections` (Finding 13, B1). */
  | 'deliveryRejections'
  /** `GET catalog-operations/stage-metadata` (B1). */
  | 'stageMetadata'
  /** `GET catalog-operations/{ref}/actions` — batch History's control-action log (#160, S9). */
  | 'runControlActions'
  /** `GET catalog-operations/{ref}/nodes` — batch History's execution tree (#160, S9). */
  | 'runNodes'
  /** `GET catalog-operations/estimate` — New batch's "Estimate" milestones (#205, S11, B1). */
  | 'runEstimate'
  /** `POST catalog-operations/trigger-filters/count` — New batch's per-filter match counts (B3). */
  | 'triggerFilterCounts'
  /** `POST catalog-operations/trigger-filters/values` — New batch's filter value pickers (B3). */
  | 'triggerFilterValues'
  /** `GET catalog-operations/syncs` — New batch's "reuse an existing sync" picker (B4). */
  | 'connectorSyncs'
  /** `GET catalog-runs/summaries` — product history rows + run-name links, batched (§89). */
  | 'runSummaries'
  /** `GET feed-products/{id}/source` — a listing's typed feed columns + verbatim record. */
  | 'feedProductSource'
  /** `GET catalog-runs/creators` — the run list's "Created by" filter options. */
  | 'runCreators';

const unavailable = new Set<CatalogCapability>();

/** False once this session has seen the backend answer "no such route" for `capability`. */
export function isCapabilityAvailable(capability: CatalogCapability): boolean {
  return !unavailable.has(capability);
}

/** Records that the deployed backend lacks `capability`, so callers stop probing it. */
export function markCapabilityUnavailable(capability: CatalogCapability): void {
  unavailable.add(capability);
}

/**
 * True when `error` means the route itself is missing (an older deploy), not that a resource is:
 * a 405, or a 404 WITHOUT catalog-service's `NOT_FOUND` code (FastAPI's unrouted 404 carries none;
 * an unknown product / run carries `error_code: NOT_FOUND`). `alsoWhen` covers a new path an older
 * deploy routes elsewhere (e.g. `experiments/status-counts` → `experiments/{uuid}` → 422).
 */
export function isMissingRoute(error: unknown, alsoWhen?: (error: ApiError) => boolean): boolean {
  if (!(error instanceof ApiError)) return false;
  if (error.statusCode === 405) return true;
  if (error.statusCode === 404 && error.serverCode !== 'NOT_FOUND') return true;
  return alsoWhen?.(error) ?? false;
}

/** True when a 422's `details.errors[]` names `field` (e.g. `path.experiment_id`). */
export function isValidationOfField(error: ApiError, field: string): boolean {
  if (error.statusCode !== 422) return false;
  const details = (error.body as { details?: { errors?: unknown } } | null)?.details;
  const errors = Array.isArray(details?.errors) ? (details.errors as unknown[]) : [];
  return errors.some(
    (entry) =>
      typeof entry === 'object' && entry !== null && (entry as { field?: unknown }).field === field,
  );
}

/** What `readWithFallback` hands back: the data, tagged with the path that produced it. */
export type FallbackResult<TNew, TOld> =
  | { source: 'new'; data: TNew }
  | { source: 'fallback'; data: TOld };

interface ReadWithFallbackOptions<TNew, TOld> {
  capability: CatalogCapability;
  /** The new read. */
  read: () => Promise<TNew>;
  /** Today's read path, used when the new one is unavailable. */
  fallback: () => Promise<TOld>;
  /** Optional "the response lacks the new shape" check — treated like a missing route. */
  isComplete?: (data: TNew) => boolean;
  /** Extra "route missing" signature for this path (see `isMissingRoute`). */
  missingWhen?: (error: ApiError) => boolean;
}

/**
 * Tries the new read unless this session already knows it is unavailable; on a missing route (or
 * an incomplete response) marks it unavailable and returns the fallback's data instead. Any other
 * error is the new read's real error and propagates (retry / error UI as usual).
 */
export async function readWithFallback<TNew, TOld>({
  capability,
  read,
  fallback,
  isComplete,
  missingWhen,
}: ReadWithFallbackOptions<TNew, TOld>): Promise<FallbackResult<TNew, TOld>> {
  if (isCapabilityAvailable(capability)) {
    try {
      const data = await read();
      if (!isComplete || isComplete(data)) return { source: 'new', data };
      markCapabilityUnavailable(capability);
    } catch (error) {
      if (!isMissingRoute(error, missingWhen)) throw error;
      markCapabilityUnavailable(capability);
    }
  }
  return { source: 'fallback', data: await fallback() };
}

/** True when `obj` carries `key` at all (a deployed backend sends new keys, null or not). */
export function hasField(obj: object | null | undefined, key: string): boolean {
  return obj !== null && obj !== undefined && key in obj;
}
